import { useIsValidatedBayerUserType } from '/@/hooks/session';
import { _ } from '/@/locale';
import type { CropMonitoring } from '/@/types/crop-monitoring';
import type { Field } from '/@/types/field';
import type { StageMessage } from './shared';
import { useProtectionActivity } from './use-protection-activity';

/**
 * "VN" banner — corn only, advisor-facing. Covers every vegetative stage from V8 onward
 * (`phenology === 'emergence'` with `vegetativeStage >= 8`) — earlier vegetative stages have
 * their own banners (V2/V3/V5/V7).
 *
 * Shown when no fungicide/insecticide application or recommendation is registered yet. This is
 * the optimal fungicide window, so the message names the current stage and the action
 * pre-selects the fungicide (disease) flow.
 *
 * Returns `null` before V8, for non-advisor users, or once some fungicide/insecticide activity
 * is registered.
 */
export const useVNStageMessage = ({
    cropMonitoring,
    field,
}: {
    cropMonitoring?: CropMonitoring;
    field: Field;
}): StageMessage | null => {
    const isValidatedBayerUserType = useIsValidatedBayerUserType();

    const corn = cropMonitoring?.cornCropMonitoring;
    const vegetativeStage = corn?.vegetativeStage;
    const isCornVN =
        Boolean(cropMonitoring?.active) &&
        cropMonitoring?.crop === 'corn' &&
        corn?.phenology === 'emergence' &&
        vegetativeStage != null &&
        vegetativeStage >= 8;

    const showCandidate = isValidatedBayerUserType && isCornVN;

    const protection = useProtectionActivity({ cropMonitoring, field, enabled: showCandidate });

    if (!showCandidate) return null;

    // Hold until the gating queries resolve, then only prompt while no protection activity exists.
    if (protection.isLoading) return null;
    if (protection.hasActivity) return null;

    return {
        title: _('Ventana óptima para recomendar fungicida'),
        message: _(
            'El cultivo está en {{stage}}. Recomendá el fungicida y compartilo con el productor para proteger el rendimiento contra enfermedades.',
            { stage: `V${vegetativeStage}` }
        ),
        secondaryAction: { label: _('Registrar aplicación'), action: 'register-application' },
        primaryAction: { label: _('Recomendar fungicida'), action: 'recommend-fungicide' },
    };
};
